// src/components/MainTable/ProjectsTable.tsx
// Requires: npm i fuse.js
import type { User } from 'src/types/user';

import React, { useEffect, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { toast } from 'sonner';

import { useAuth } from 'src/AuthContext';
import { takeoffUrl, designToolUrl } from 'src/globalSettings';
import { openFromSelection } from 'src/utils/iframeNavigation';

import { updateActiveVersion } from '../../services/projects';

import type { Project, ProjectGroup } from '../../types/project';
import { timeAgo } from 'src/utils/timeAgo';
import RowActions from './RowActions';

type Props = {
  view: 'all' | 'user';
  searchText: string;
  statusFilter: 'All' | 'Quoted' | 'Draft';
  users: User[];
  projects: ProjectGroup[];
  selectedOwner: string | undefined;
};

const handleEdit = (project: Project, isRetail: boolean) => {
  if (project.projectType?.toString().toLowerCase() === 'blueprint' && !project.takeoffSubmitted) {
    openFromSelection(`${takeoffUrl}/?project_id=${project.id}`);
  } else {
    let url = `${designToolUrl}/?project_id=${project.id}`;
    if (isRetail) url += '&retail=true';
    openFromSelection(url);
  }
};

const pickActive = (group: ProjectGroup): Project | undefined => {
  // Prefer explicit `active` on project.
  const flagged = group.projects?.find((p) => p.active);
  if (flagged) return flagged;

  // Fallback: parent.activeProjectId matches a project id.
  const byParent =
    group.parentProject?.activeProjectId &&
    group.projects?.find((p) => p.id === group.parentProject.activeProjectId);
  if (byParent) return byParent;

  // Final fallback: first project if present.
  return group.projects?.[0];
};

const ProjectsTable: React.FC<Props> = ({
  view,
  searchText,
  statusFilter,
  users,
  projects,
  selectedOwner = 'All',
}) => {
  const { user } = useAuth();
  const isRetail = useMemo(() => !user.isMultifamily, [user]);

  // Local copy for UI-only mutations and searching
  const [localGroups, setLocalGroups] = useState<ProjectGroup[]>(projects);

  useEffect(() => {
    setLocalGroups(projects);
  }, [projects, view]);

  // Normalize a view of groups with a computed active reference, without mutating source.
  const searchable = useMemo(
    () =>
      (localGroups ?? [])
        .filter((g) => g.projects?.length)
        .map((g) => {
          const active = pickActive(g);
          return {
            group: g,
            activeProject: active,
            name: active?.name || '',
            customer: active?.customer || active?.customerName || '',
            owner: active?.owner || '',
          };
        }),
    [localGroups],
  );

  const fuse = useMemo(
    () =>
      new Fuse(searchable, {
        keys: ['name', 'customer', 'owner'],
        threshold: 0.3,
        ignoreLocation: true,
        includeScore: false,
      }),
    [searchable],
  );

  const filteredGroups = useMemo(() => {
    const applyOwner = (g: ProjectGroup, active: Project | undefined) => {
      if (view !== 'all' || selectedOwner === 'All') return true;

      // Parent owners is string[] of emails/user ids.
      const parentOwners = g.parentProject?.owners ?? [];
      const projectOwner = active?.owner ?? '';

      return parentOwners.includes(selectedOwner) || projectOwner === selectedOwner;
    };

    const applyStatus = (_active: Project | undefined) => {
      if (statusFilter === 'All') return true;
      return (_active as any)?.status === statusFilter;
    };

    let candidateGroups: ProjectGroup[];

    if (searchText.trim().length > 0) {
      candidateGroups = fuse.search(searchText.trim()).map((r) => r.item.group);
    } else {
      candidateGroups = searchable.map((s) => s.group);
    }

    return candidateGroups.filter((g) => {
      const active = pickActive(g);
      return applyOwner(g, active) && applyStatus(active);
    });
  }, [fuse, searchable, searchText, selectedOwner, view, statusFilter]);

  const handleSetActiveVersion = async (parentProjectId?: string, newActiveProjectId?: string) => {
    if (!parentProjectId || !newActiveProjectId) return;

    // Optimistic local update. Do not change server-selected defaults beyond the explicit choice.
    setLocalGroups((prev) =>
      prev.map((group) => {
        if (group.parentProject?.id === parentProjectId && group.projects) {
          return {
            ...group,
            parentProject: {
              ...group.parentProject,
              activeProjectId: newActiveProjectId,
              activeProjectName:
                group.projects.find((p) => p.id === newActiveProjectId)?.versionName ||
                group.parentProject.activeProjectName,
            },
            projects: group.projects.map((p) => ({
              ...p,
              active: p.id === newActiveProjectId,
            })),
          };
        }
        return group;
      }),
    );

    await updateActiveVersion(parentProjectId, newActiveProjectId);
    toast.success('Active version updated');
  };

  const handleDeleteParentProjectLocally = (parentProjectId: string) => {
    setLocalGroups((prev) => prev.filter((g) => g.parentProject?.id !== parentProjectId));
  };

  const handleDeleteVersion = (parentProjectId: string, deletedProjectId: string) => {
    setLocalGroups((prev) =>
      prev.flatMap((group) => {
        if (group.parentProject?.id !== parentProjectId) return [group];

        const remaining = (group.projects ?? []).filter((p) => p.id !== deletedProjectId);

        if (remaining.length === 0) return [];

        const nextActive =
          [...remaining].sort(
            (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime(),
          )[0] ?? remaining[0];

        const normalized = remaining.map((p) => ({ ...p, active: p.id === nextActive.id }));

        return [
          {
            ...group,
            parentProject: {
              ...group.parentProject,
              activeProjectId: nextActive.id!,
              activeProjectName: nextActive.versionName || group.parentProject.activeProjectName,
            },
            projects: normalized,
          },
        ];
      }),
    );
  };

  const handleUpdateSalesforceId = (parentProjectId: string, sfId: string) => {
    setLocalGroups((prev) =>
      prev.map((group) => {
        if (group.parentProject?.id === parentProjectId && group.projects) {
          return {
            ...group,
            projects: group.projects.map((p) => ({ ...p, salesforceId: sfId })),
          };
        }
        return group;
      }),
    );
  };

  if (!localGroups) {
    return (
      <div className='text-center py-5'>
        <span>Loading...</span>
      </div>
    );
  }

  if (filteredGroups.length === 0) {
    return (
      <div className='text-center py-5'>
        <span>No projects found</span>
      </div>
    );
  }

  return (
    <div className='projects-grid'>
      {filteredGroups.map((group) => {
        const displayProject = pickActive(group);
        if (!displayProject) return null;
        return (
          <div
            key={displayProject.id}
            className='project-card'
            onClick={() => handleEdit(displayProject, isRetail)}
          >
            <div className='project-card-top-wrapper'>
              <img src='/project-preview.webp' width='100%' height='100%' alt='' />
              <p className='badge'>{displayProject.projectType}</p>
            </div>

            <div className='project-card-bottom-wrapper'>
              <p className='title'>{displayProject.name}</p>
              <p className='detail'>Version: {displayProject.versionName}</p>
              <div className='project-card-row'>
                <p className='detail'>{displayProject.unitNumbersQty} Units</p>
                <svg height='4' width='4' xmlns='http://www.w3.org/2000/svg'>
                  <circle r='2' cx='2' cy='2' fill='#9CA3AF' />
                </svg>
                <p className='detail'>Edited {timeAgo(displayProject.updatedAt!)}</p>
              </div>
              {view == 'all' && selectedOwner == 'All' && (
                <p
                  style={{
                    fontSize: '14px',
                  }}
                  className='detail'
                >
                  {displayProject.owner}
                </p>
              )}
              {/* {
                displayProject?.projectType?.toLowerCase() === 'blueprint' &&  statusInfo && statusInfo?.status !== 'NOT_FOUND'  && (
                    <Progress percent={statusInfo?.progress_percent} status={status} />
                )
              } */}
            </div>

            <div className='project-card-actions' onClick={(e) => e.stopPropagation()}>
              <RowActions
                project={displayProject}
                otherVersions={
                  group.projects
                    ?.filter((p) => p.id !== displayProject.id)
                    .map((p) => ({ id: p.id!, name: p.versionName || 'Unnamed Version' })) ?? []
                }
                users={users}
                onSetActiveVersion={(newProjectId) =>
                  handleSetActiveVersion(group.parentProject?.id, newProjectId)
                }
                onDeleteParentProjectLocally={handleDeleteParentProjectLocally}
                onDeleteVersion={(projectId) =>
                  handleDeleteVersion(group.parentProject.id!, projectId)
                }
                onUpdateSalesforceId={handleUpdateSalesforceId}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectsTable;
